import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, CircularProgress, IconButton, MenuItem, Select, TextField, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, OutlinedInput, Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { getUserData } from '../../../../utils/FunctionHelpers';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { getConsultantsAndManagers } from '../../../../services/UserService';
import { getCandidatClientMeta, updateCandidatClientMetaAttribute } from '../../../../services/ProcessService';
import { listEmailTypes } from '../../../../services/EmailTypeService';
import { getAllProcesses, sendEmailClientMeta } from '../../../../services/ProcessService';
import DialogDeleteCustom from '../../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../utils/gridStyles';
import { DataGrid, smartSortComparator } from '../../../../components/SortableDataGrid/SortableDataGrid';


const toJsDate = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return new Date(v);
  if (v && typeof v === 'object' && v.date) return new Date(v.date);
  return null;
};

const SmallSpinner = () => <CircularProgress size={16} sx={{ ml: 1 }} />;

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Shown under the "PROCESS INTERN" tab: Go&Dev internal steps (préqualif RH, testeur, test technique)
const ProcessInterneHint = () => (
  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mr: 'auto', ml: -0.5 }}>
    Étapes dans l'ordre : Préqualif RH → résultat → testeur → mail au testeur → test technique → résultat.
  </Typography>
);
const CustomToolbar = makeGridToolbar('Process interne', ProcessInterneHint);

const NoteTooltipIcon = ({ note, title = 'Note' }) => (
  <Tooltip title={note && note.trim() !== '' ? note : 'Aucune note'}>
    <IconButton size="small" aria-label={title}><HelpOutlineIcon fontSize="small" /></IconButton>
  </Tooltip>
);

const ProcessClient = ({ snackbarShowMessage, idCandidat: idCandidatProp }) => {
  const { idCandidat: idFromRoute } = useParams();
  const idCandidat = idCandidatProp ?? idFromRoute ?? null;

  const [loading, setLoading] = useState(true);
  const [dataRows, setDataRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managers, setManagers] = useState([]);
  const [sending, setSending] = useState(false);
  const [emailTypes, setEmailTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [saving, setSaving] = useState({});
  // { row, date } while the "edit test technique date" dialog is open
  const [testTechEdit, setTestTechEdit] = useState(null);

  const [openTestTechNote, setOpenTestTechNote] = useState(false);
  const [pendingNote, setPendingNote] = useState('');
  const [pendingRow, setPendingRow] = useState(null);

  const [openPrequalifNote, setOpenPrequalifNote] = useState(false);
  const [pendingPreqNote, setPendingPreqNote] = useState('');
  const [pendingPreqRow, setPendingPreqRow] = useState(null);

  const [openMailDialog, setOpenMailDialog] = useState(false);
  const [mailRow, setMailRow] = useState(null);
  const [mailForm, setMailForm] = useState({ from: '', to: '', cc: '', cci: '', subject: '', message: '' });
  const [errors, setErrors] = useState({ to: false, subject: false });
  const [mailDestination, setMailDestination] = useState('');
  const [mailTesterPrenom, setMailTesterPrenom] = useState('');

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loadingToDelete, setLoadingToDelete] = useState(false);

  const cellKey = (pid, attr) => `${pid}:${attr}`;

  const patchRow = (pid, field, value) => {
    setDataRows((prev) => prev.map((r) => (r.processId === pid ? { ...r, [field]: value } : r)));
  };

  const handleAttributeUpdate = async (row, attribute, value, opts = {}) => {
    // Client-meta rows have no processId: keep it as-is (undefined) so patchRow and
    // the cells' saving keys (cellKey(row.processId, ...)) refer to the same row.
    const pid = row?.processId;
    const key = cellKey(pid, attribute);
    let sendVal = value;
    if (opts.isDate) sendVal = value ? moment(value).format('YYYY-MM-DD') : null;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await updateCandidatClientMetaAttribute(idCandidat, attribute, sendVal);
      patchRow(pid, attribute, sendVal);
      if (opts.toast !== false) snackbarShowMessage('Mise à jour effectuée');
    } catch (e) {
      snackbarShowMessage(e?.response?.data?.error?.detail || 'Erreur lors de la mise à jour', 'error', 4000);
    } finally {
      setSaving((s) => { const { [key]: _, ...rest } = s; return rest; });
    }
  };

  const setPrequalifResult = async (row, value) => {
    patchRow(row.processId, 'prequalifClientRhResult', value);
    await handleAttributeUpdate(row, 'prequalifClientRhResult', value, { toast: false });
    openPrequalifNoteDialog(row, row.prequalifClientRhNote);
  };

  const setTestTechniqueResult = async (row, value) => {
    patchRow(row.processId, 'testTechniqueResult', value);
    await handleAttributeUpdate(row, 'testTechniqueResult', value, { toast: false });
    openTestTechNoteDialog(row, row.testTechniqueNote);
  };

  const openTestTechNoteDialog = (row, currentValue) => {
    setPendingRow(row); setPendingNote(currentValue || ''); setOpenTestTechNote(true);
  };
  const closeTestTechNoteDialog = () => { setOpenTestTechNote(false); setPendingRow(null); setPendingNote(''); };
  const saveTestTechNote = async () => {
    if (!pendingRow) return;
    await handleAttributeUpdate(pendingRow, 'testTechniqueNote', pendingNote);
    closeTestTechNoteDialog();
  };

  const openPrequalifNoteDialog = (row, currentValue = '') => {
    setPendingPreqRow(row); setPendingPreqNote(currentValue || ''); setOpenPrequalifNote(true);
  };
  const closePrequalifNoteDialog = () => { setOpenPrequalifNote(false); setPendingPreqRow(null); setPendingPreqNote(''); };
  const savePrequalifNote = async () => {
    if (!pendingPreqRow) return;
    await handleAttributeUpdate(pendingPreqRow, 'prequalifClientRhNote', pendingPreqNote);
    closePrequalifNoteDialog();
  };

  const handleSourceurChange = async (processId, newSourceurUuid) => {
    const key = cellKey(processId, 'sourceur');
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await updateCandidatClientMetaAttribute(idCandidat, 'sourceur', newSourceurUuid ?? null);
      patchRow(processId, 'sourceur', newSourceurUuid || null);
      const selectedTester = managers.find((m) => m.uuid === newSourceurUuid);
      setMailDestination(selectedTester?.email);
      setMailTesterPrenom(selectedTester?.user?.split(' ')[0] || '');
      snackbarShowMessage('Testeur mis à jour');
    } catch (error) {
      snackbarShowMessage(error?.response?.data?.error?.detail || 'Maj testeur échouée', 'error', 4000);
    } finally {
      setSaving((s) => { const { [key]: _, ...rest } = s; return rest; });
    }
  };

  const openMailDialogFor = (row) => {
    // Recipient = the tester chosen in step 2 (falls back to the last selection).
    const selectedTester = managers.find((m) => m.uuid === row.sourceur);
    const testerEmail = selectedTester?.email || mailDestination;
    const testerPrenom = selectedTester?.user?.split(' ')[0] || mailTesterPrenom;
    setMailRow(row);
    setMailForm({
      from: getUserData()?.email || '',
      to: testerEmail || '',
      cc: '', cci: '',
      subject: 'GO&DEV - Candidat à tester',
      message: `Bonjour ${testerPrenom ? testerPrenom + ',' : ','}\n\nQuelles sont tes disponibilités dans les tout prochains jours pour échanger avec ce profil ?\nMerci de ta réponse que je puisse organiser l'entretien rapidement.\nJ'attends ton retour`,
    });
    setOpenMailDialog(true);
  };
  const closeMailDialog = () => { setOpenMailDialog(false); setMailRow(null); };
  const handleMailFieldChange = (e) => { const { name, value } = e.target; setMailForm((prev) => ({ ...prev, [name]: value })); };
  const handleEmailTypeChange = (uuid) => {
    const selected = emailTypes.find((t) => t.uuid === uuid);
    setMailForm((prev) => ({ ...prev, subject: selected?.objet || prev.subject, message: htmlToText(selected?.message || ''), emailTypeUuid: uuid }));
  };

  const htmlToText = (html) => {
    if (!html) return '';
    let s = html.replace(/<\/p>\s*<p>/gi, '\n\n').replace(/<br\s*\/?>/gi, '\n').replace(/<\/(div|section|article|h[1-6]|ul|ol|table)>\s*/gi, '\n').replace(/<\/li>\s*/gi, '\n- ').replace(/<li[^>]*>\s*/gi, '- ');
    const div = document.createElement('div');
    div.innerHTML = s;
    s = (div.textContent || div.innerText || '').trim();
    s = s.replace(/\n-\s*\n/g, '\n- ').replace(/\n{3,}/g, '\n\n');
    return s;
  };

  const emailsTo = (mailForm.to || '').split(',').map((e) => e.trim()).filter((e) => e.length > 0);
  const allValidTo = emailsTo.length === 0 || emailsTo.every(isValidEmail);

  const handleSendMail = async () => {
    if (!mailForm.to.trim() || !mailForm.subject.trim()) {
      setErrors({ to: !mailForm.to.trim(), subject: !mailForm.subject.trim() });
      snackbarShowMessage('Veuillez remplir les champs obligatoires.', 'error', 4000);
      return;
    }
    const emailsCc = (mailForm.cc || '').split(',').map((e) => e.trim()).filter(Boolean);
    const emailsCci = (mailForm.cci || '').split(',').map((e) => e.trim()).filter(Boolean);
    if (!allValidTo || !emailsCc.every(isValidEmail) || !emailsCci.every(isValidEmail)) {
      setErrors((prev) => ({ ...prev, to: !allValidTo || prev.to }));
      snackbarShowMessage('Adresse e-mail invalide (À / CC / CCI).', 'error', 4000);
      return;
    }
    setErrors({ to: false, subject: false });
    if (!mailRow) return;
    const now = new Date();
    try {
      setSending(true);
      const me = getUserData() || {};
      const fd = new FormData();
      const plainMessage = htmlToText(mailForm.message);
      fd.append('de', me.uuid || '');
      fd.append('to', (mailForm.to || '').trim());
      fd.append('cc', (mailForm.cc || '').trim());
      fd.append('cci', (mailForm.cci || '').trim());
      fd.append('objet', mailForm.subject || 'GO&DEV - Envoi du candidat');
      fd.append('message', plainMessage);
      fd.append('candidat', idCandidat);
      fd.append('stats', '0');
      fd.append('date', moment(now).format());
      fd.append('dateIntegration', '');
      fd.append('displayPhoneNumber', '1');
      await sendEmailClientMeta(fd);
      const today = moment(now).format('YYYY-MM-DD');
      await handleAttributeUpdate(mailRow, 'mailSentAt', today, { isDate: false, toast: false });
      snackbarShowMessage('Email envoyé et date enregistrée', 'success', 4000);
      closeMailDialog();
    } catch (e) {
      snackbarShowMessage(e?.response?.data?.error?.detail || e?.message || "Erreur lors de l'envoi du mail", 'error', 5000);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteProcess = (e) => { e.preventDefault(); setOpenDeleteDialog(true); };
  const handleCloseDeleteDialog = () => { if (!loadingToDelete) setOpenDeleteDialog(false); };
  const resetProcess = async () => {
    setLoadingToDelete(true);
    const attributes = ['sourceur', 'prequalifClientRhAt', 'prequalifClientRhResult', 'prequalifClientRhNote', 'mailSentAt', 'testTechniqueAt', 'testTechniqueResult', 'testTechniqueNote'];
    try {
      await Promise.all(attributes.map((f) => updateCandidatClientMetaAttribute(idCandidat, f, null)));
      // Clear the cards right away (no reload needed)
      setDataRows((rows) => rows.map((r) => ({ ...r, ...Object.fromEntries(attributes.map((f) => [f, null])) })));
      snackbarShowMessage('Process interne réinitialisé');
      setOpenDeleteDialog(false);
    } catch (e) {
      snackbarShowMessage(e?.response?.data?.error?.detail || 'Erreur lors de la réinitialisation', 'error', 4000);
    } finally {
      setLoadingToDelete(false);
    }
  };

  // A process deleted (or added) from the "Process intern" tab disappears here too.
  useEffect(() => {
    const onProcessChanged = (e) => {
      const deletedId = e.detail?.processId;
      if (deletedId) {
        setDataRows((rows) => rows.filter((r) => r.processId !== deletedId));
        setCount((c) => Math.max(0, c - 1));
      }
    };
    window.addEventListener('candidat-process-changed', onProcessChanged);
    return () => window.removeEventListener('candidat-process-changed', onProcessChanged);
  }, []);

  // Testers and the internal process load in parallel; the grid shows its loading
  // skeleton from the first render (no "Aucun enregistrement" flash while waiting).
  useEffect(() => {
    if (!idCandidat) return undefined;
    let active = true;
    setLoading(true);
    setLoadingManagers(true);

    getConsultantsAndManagers()
      .then((res) => { if (active) setManagers(res?.data?.managers?.filter((user) => user.role === 'ROLE_TESTEUR') || []); })
      .catch(() => { if (active) snackbarShowMessage('Erreur de chargement des testeurs.', 'error', 4000); })
      .finally(() => { if (active) setLoadingManagers(false); });

    getCandidatClientMeta(idCandidat)
      .then(({ data }) => {
        if (!active) return;
        const arr = (Array.isArray(data) ? data : [data]).filter(Boolean);
        // The internal process is one row per candidate, created on the first save:
        // always show it so the user can start with the Préqualif RH.
        const rows = arr.length ? arr : [{}];
        setDataRows(rows.map((r, i) => ({ ...r, id: r.processId ?? i })));
        setCount(rows.length);
      })
      .catch((e) => {
        if (!active) return;
        setDataRows([{ id: 0 }]);
        setCount(1);
        snackbarShowMessage(e?.response?.data?.error?.detail || 'Erreur de chargement du process interne', 'error', 4000);
      })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCandidat]);

  // Email templates: only needed when the "mail au testeur" dialog opens.
  useEffect(() => {
    if (!openMailDialog) return undefined;
    let active = true;
    setLoadingTypes(true);
    listEmailTypes()
      .then((res) => { if (active) setEmailTypes((res?.data || []).filter((t) => t.TypeMsg === 'email')); })
      .catch(() => { if (active) snackbarShowMessage("Erreur lors du chargement des types d'email.", 'error', 4000); })
      .finally(() => { if (active) setLoadingTypes(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMailDialog]);

  if (!idCandidat) {
    return (
      <Box p={2}>
        <strong>ID candidat manquant.</strong>
        <div>Ouvrez cette page via une URL contenant <code>:idCandidat</code> ou passez <code>idCandidat</code> en prop.</div>
      </Box>
    );
  }

  const hasValue = (v) => v !== null && v !== undefined && v !== '' && v !== false;

  const columns = [
    {
      field: 'prequalifClientRhAt', headerName: 'PRÉQUALIF RH', width: 200,
      renderCell: (p) => {
        const row = p.row;
        const pid = row.processId;
        const isSaving = !!saving[cellKey(pid, 'prequalifClientRhAt')];
        const hasPrequalifResult = hasValue(row.prequalifClientRhAt);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!hasPrequalifResult ? (
              <DatePicker
                enableAccessibleFieldDOMStructure
                value={toJsDate(p.value)}
                onChange={(date) => handleAttributeUpdate(row, 'prequalifClientRhAt', date, { isDate: true })}
                format="dd-MM-yyyy" sx={{ width: 170 }} disabled={isSaving}
                renderInput={(params) => <TextField {...params} />}
              />
            ) : (
              <Typography sx={{ fontSize: '1rem', color: '#3b82f6' }}>
                {moment(p.value).format('DD/MM/YYYY')}
              </Typography>
            )}
            {isSaving && <SmallSpinner />}
          </Box>
        );
      },
    },
    {
      field: 'prequalifClientRhResult', headerName: 'RÉSULTAT', width: 160,
      renderCell: (p) => {
        const row = p.row;
        const pid = row.processId;
        const isSaving = !!saving[cellKey(pid, 'prequalifClientRhResult')];
        const hasPrequalifDate = hasValue(row.prequalifClientRhAt);
        const disabled = !hasPrequalifDate || isSaving;
        const hasResult = p.value === true || p.value === false;
        return (
          <Tooltip title={!hasPrequalifDate ? 'Complétez la date avant de choisir le résultat' : ''}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: disabled ? 0.6 : 1 }}>
              {hasResult ? (
                <>
                  {p.value ? <Check sx={{ color: '#28A745' }} /> : <Close sx={{ color: '#EF6262' }} />}
                  <span onClick={() => !disabled && openPrequalifNoteDialog(row, row.prequalifClientRhNote)} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                    <NoteTooltipIcon note={row.prequalifClientRhNote} title="Note préqualif RH" />
                  </span>
                </>
              ) : (
                <>
                  <IconButton title="Marquer KO" onClick={() => !disabled && setPrequalifResult(row, false)} disabled={disabled}><Close /></IconButton>
                  <IconButton title="Marquer OK" onClick={() => !disabled && setPrequalifResult(row, true)} disabled={disabled}><Check /></IconButton>
                  <NoteTooltipIcon note={row.prequalifClientRhNote} title="Note préqualif RH" />
                </>
              )}
              {isSaving && <SmallSpinner />}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'sourceur', headerName: 'TESTEUR', width: 220,
      sortComparator: (a, b) => smartSortComparator(managers.find((m) => m.uuid === a)?.user, managers.find((m) => m.uuid === b)?.user),
      renderCell: (p) => {
        const row = p.row;
        const pid = row.processId;
        const isSaving = !!saving[cellKey(pid, 'sourceur')];
        const hasPrequalifResult = hasValue(row.prequalifClientRhAt) && row.prequalifClientRhResult !== false;
        const disabled = !hasPrequalifResult || isSaving;
        const hasMailSentAt = hasValue(row.mailSentAt);

        if (loadingManagers) return <CircularProgress size={18} />;
        return (
          <Tooltip title={!hasPrequalifResult ? 'Complétez le résultat avant de choisir le testeur' : ''}>
            <Box sx={{ display: 'flex', alignItems: 'center', opacity: disabled ? 0.6 : 1 }}>
              <Select value={p.value || ''} displayEmpty size="small"
                onChange={(e) => !disabled && handleSourceurChange(pid, e.target.value || null)}
                sx={{ minWidth: 180 }} disabled={disabled || hasMailSentAt}>
                <MenuItem value=""><em>-</em></MenuItem>
                {managers.map((m) => <MenuItem key={m.uuid} value={m.uuid}>{m.user ?? m.fullName ?? m.email}</MenuItem>)}
              </Select>
              {isSaving && <SmallSpinner />}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'mailSentAt', headerName: 'MAIL (ENVOI)', width: 160,
      renderCell: (p) => {
        const row = p.row;
        const sent = toJsDate(p.value);
        const hasSourceur = hasValue(row.sourceur) && row.prequalifClientRhResult !== false;
        const disabled = !hasSourceur;
        return (
          <Tooltip title={!hasSourceur ? 'Sélectionnez un testeur avant d\'envoyer le mail' : ''}>
            <Box sx={{ opacity: disabled ? 0.6 : 1 }}>
              {sent ? (
                <Typography sx={{ fontSize: '1rem', color: '#3b82f6' }}>{moment(sent).format('DD/MM/YYYY')}</Typography>
              ) : (
                <Button size="small" variant="outlined" disabled={disabled} onClick={() => !disabled && openMailDialogFor(row)}>Envoyer</Button>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'testTechniqueAt', headerName: 'TEST TECHNIQUE', width: 200,
      renderCell: (p) => {
        const row = p.row;
        const pid = row.processId;
        const isSaving = !!saving[cellKey(pid, 'testTechniqueAt')];
        const hasMail = hasValue(row.mailSentAt) && row.prequalifClientRhResult !== false;
        const disabled = !hasMail || isSaving;
        const hasTestResult = hasValue(row.testTechniqueResult);
        return (
          <Tooltip title={!hasMail ? 'Envoyez le mail avant de planifier le test technique' : ''}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', opacity: disabled ? 0.6 : 1 }}>
              {!hasTestResult ? (
                <DatePicker
                  enableAccessibleFieldDOMStructure
                  value={toJsDate(p.value)}
                  onChange={(date) => !disabled && handleAttributeUpdate(row, 'testTechniqueAt', date, { isDate: true })}
                  format="dd-MM-yyyy" sx={{ width: 170 }} disabled={disabled}
                  renderInput={(params) => <TextField {...params} />}
                />
              ) : (
                <>
                  <Typography sx={{ fontSize: '1rem', color: '#3b82f6' }}>{moment(p.value).format('DD/MM/YYYY')}</Typography>
                  <IconButton size="small" title="Modifier la date" onClick={() => setTestTechEdit({ row, date: toJsDate(p.value) })} disabled={isSaving}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </>
              )}
              {isSaving && <SmallSpinner />}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'testTechniqueResult', headerName: 'RÉSULTAT', width: 160,
      renderCell: (p) => {
        const row = p.row;
        const pid = row.processId;
        const isSaving = !!saving[cellKey(pid, 'testTechniqueResult')];
        const hasTestDate = hasValue(row.testTechniqueAt) && row.prequalifClientRhResult !== false;
        const disabled = !hasTestDate || isSaving;
        const hasResult = p.value === true || p.value === false;
        return (
          <Tooltip title={!hasTestDate ? 'Ajoutez une date de test avant de définir le résultat' : ''}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: disabled ? 0.6 : 1 }}>
              {hasResult ? (
                <>
                  {p.value ? <Check sx={{ color: '#28A745' }} /> : <Close sx={{ color: '#EF6262' }} />}
                  <span onClick={() => !disabled && openTestTechNoteDialog(row, row.testTechniqueNote)} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
                    <NoteTooltipIcon note={row.testTechniqueNote} title="Note test technique" />
                  </span>
                </>
              ) : (
                <>
                  <IconButton title="Marquer KO" onClick={() => !disabled && setTestTechniqueResult(row, false)} disabled={disabled}><Close /></IconButton>
                  <IconButton title="Marquer OK" onClick={() => !disabled && setTestTechniqueResult(row, true)} disabled={disabled}><Check /></IconButton>
                  <NoteTooltipIcon note={row.testTechniqueNote} title="Note test technique" />
                </>
              )}
              {isSaving && <SmallSpinner />}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions', headerName: 'ISSUE', width: 130, sortable: false,
      renderCell: (p) => {
        const row = p.row;
        return row.prequalifClientRhAt != null ? (
          <IconButton onClick={(e) => handleDeleteProcess(e)} sx={{ color: '#ef4444' }}><Delete /></IconButton>
        ) : null;
      },
    },
  ];

  return (
    <>
      <DialogDeleteCustom
        handleClose={handleCloseDeleteDialog} open={openDeleteDialog} handleConfirm={resetProcess}
        loading={loadingToDelete} label="Voulez vous supprimer le process?" title="Supprimer le process?"
      />
      <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '45vh', minHeight: 300, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}>
        <DataGrid
          rows={dataRows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pageSizeOptions={[10, 50]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>

      <Dialog open={openMailDialog} onClose={closeMailDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: 'rgb(17,122,167)', fontWeight: 700 }}>Mail d'envoi au testeur</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>DE</Typography>
          <FormControl fullWidth size="small">
            <InputLabel shrink sx={{ display: 'none' }}>DE</InputLabel>
            <Select value={mailForm.from || getUserData()?.email || ''} disabled input={<OutlinedInput notched />}>
              <MenuItem value={getUserData()?.email || ''}>
                {(getUserData()?.fullName || getUserData()?.user || '') + (getUserData()?.email ? ` (${getUserData()?.email})` : '')}
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>À <span style={{ color: '#d32f2f' }}>*</span></Typography>
          <TextField id="to" name="to" placeholder="Ajouter des destinataires" value={mailForm.to} onChange={handleMailFieldChange} fullWidth required size="small"
            error={errors.to || (mailForm.to.trim() !== '' && !allValidTo)}
            helperText={errors.to ? 'Champ obligatoire' : mailForm.to.trim() !== '' && !allValidTo ? 'Adresse e-mail invalide' : ''} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>CC</Typography>
          <TextField id="cc" name="cc" placeholder="Ajouter des CC" value={mailForm.cc} onChange={handleMailFieldChange} fullWidth size="small" autoComplete="off" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>CCI</Typography>
          <TextField id="cci" name="cci" placeholder="Ajouter des CCI" value={mailForm.cci} onChange={handleMailFieldChange} fullWidth size="small" autoComplete="off" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>Email Type</Typography>
          <FormControl fullWidth size="small">
            <InputLabel shrink sx={{ display: 'none' }}>Email Type</InputLabel>
            <Select value={mailForm.emailTypeUuid || ''} onChange={(e) => handleEmailTypeChange(e.target.value)} displayEmpty input={<OutlinedInput notched />}>
              <MenuItem value="">{loadingTypes ? 'Chargement...' : '-'}</MenuItem>
              {!loadingTypes && emailTypes.map((t) => <MenuItem key={t.uuid} value={t.uuid}>{t.msgName}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>Objet <span style={{ color: '#d32f2f' }}>*</span></Typography>
          <TextField id="subject" name="subject" placeholder="Objet du mail." value={mailForm.subject} onChange={handleMailFieldChange} fullWidth required size="small"
            error={errors.subject} helperText={errors.subject ? 'Champ obligatoire' : ''} autoComplete="off" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#203349' }}>Message <span style={{ color: '#d32f2f' }}>*</span></Typography>
          <TextField id="message" name="message" value={mailForm.message} onChange={handleMailFieldChange} fullWidth required multiline minRows={8} placeholder={'Bonjour [PRENOM],\n\nBien cordialement'} autoComplete="off" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeMailDialog} disabled={sending}>Annuler</Button>
          <Box sx={{ position: 'relative' }}>
            <Button variant="contained" color="primary" onClick={handleSendMail} disabled={sending} sx={{ minWidth: 180 }}>
              {sending ? 'Envoi en cours...' : 'Envoyer & enregistrer'}
            </Button>
            {sending && <CircularProgress size={24} sx={{ color: 'white', position: 'absolute', top: '50%', left: '50%', mt: '-12px', ml: '-12px' }} />}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={openTestTechNote} onClose={closeTestTechNoteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Note — Test technique</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Note" type="text" fullWidth multiline minRows={4}
            value={pendingNote} onChange={(e) => setPendingNote(e.target.value)} placeholder="Saisir la note du test technique…" slotProps={{ htmlInput: { maxLength: 2000 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTestTechNoteDialog}>Annuler</Button>
          <Button variant="contained" onClick={saveTestTechNote}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!testTechEdit} onClose={() => setTestTechEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Modifier la date du test technique</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <DatePicker
            enableAccessibleFieldDOMStructure
            value={testTechEdit?.date ?? null}
            onChange={(date) => setTestTechEdit((prev) => (prev ? { ...prev, date } : prev))}
            format="dd-MM-yyyy"
            sx={{ width: '100%' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestTechEdit(null)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={!testTechEdit?.date || isNaN(testTechEdit.date)}
            onClick={async () => {
              const { row, date } = testTechEdit;
              setTestTechEdit(null);
              await handleAttributeUpdate(row, 'testTechniqueAt', date, { isDate: true });
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPrequalifNote} onClose={closePrequalifNoteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Note — Préqualif RH client</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Note" type="text" fullWidth multiline minRows={4}
            value={pendingPreqNote} onChange={(e) => setPendingPreqNote(e.target.value)} placeholder="Saisir la note de la préqualif RH…" slotProps={{ htmlInput: { maxLength: 2000 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePrequalifNoteDialog}>Annuler</Button>
          <Button variant="contained" onClick={savePrequalifNote}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withSnackbar(ProcessClient);

